import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { history, useLocation } from '@umijs/max';
import { usePermissions } from '@/hooks/usePermissions';
import { getPendingRescheduleRequestCount, onRescheduleRequestUpdated } from '@/services/app/tenant/dispatch/rescheduleRequests';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { LS_PREFIX } from '@/constants';

const REVIEW_PATH = '/tenant/dashboard/ai-reschedule-requests';
const LS_DISMISSED_KEY = `${LS_PREFIX}ai_resched_banner_dismissed_max_id`;

/**
 * Site-wide banner shown while AI phone schedule requests (reschedules AND
 * new-appointment bookings) are waiting for review (client request: keep it
 * visible on all pages until someone clears/handles them). Rendered from the
 * layout's childrenRender for users with the review permission. A
 * "reschedule-request-updated" WS push (see onRescheduleRequestUpdated)
 * applies state the instant a request is created or accepted/rejected,
 * tenant-wide since this isn't per-user.
 *
 * No poll interval — refresh() only runs on mount, on a WS push, and on a
 * socket reconnect (a dropped push during a disconnect never arrives on its
 * own — see onSocketReconnected in rescheduleRequests.ts). Deliberately NOT
 * tied to visibilitychange — an open WebSocket stays connected while a tab is
 * merely backgrounded in every modern browser, so that trigger just fires on
 * ordinary window-switching with no real signal behind it.
 *
 * Dismissing (X) remembers the newest pending request id — the banner stays
 * hidden until a NEWER request arrives, and disappears on its own when all
 * pending requests are handled (accepted/rejected).
 */
const AiRescheduleBanner: React.FC = () => {
  const { canReviewRescheduleRequests } = usePermissions() as any;
  const location = useLocation();

  const [count, setCount] = useState(0);
  const [countNew, setCountNew] = useState(0);
  const [maxId, setMaxId] = useState(0);
  const [dismissedMaxId, setDismissedMaxId] = useState<number>(() =>
    parseInt(localStorage.getItem(LS_DISMISSED_KEY) || '0', 10) || 0,
  );

  const apply = useCallback((data?: { count?: number; max_id?: number; count_new_appointment?: number }) => {
    setCount(data?.count ?? 0);
    setMaxId(data?.max_id ?? 0);
    setCountNew(data?.count_new_appointment ?? 0);
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await getPendingRescheduleRequestCount());
    } catch {
      // Silent: the banner is informational; auth/permission errors are
      // handled globally and the next push retries.
    }
  }, [apply]);

  useEffect(() => {
    if (!canReviewRescheduleRequests) return;
    refresh();
    ensureNotificationSocketConnected();
    // Applies a push straight from its payload instead of turning around and
    // re-fetching. A signal-only push (no data — a socket reconnect, or an
    // older server) falls back to a REST re-fetch, same as the call-queue badge.
    const unsubscribeWs = onRescheduleRequestUpdated((data) => {
      if (!data) {
        refresh();
        return;
      }
      apply(data);
    });
    return unsubscribeWs;
  }, [canReviewRescheduleRequests, refresh, apply]);

  // Re-check when navigating, so handling requests updates the banner promptly.
  useEffect(() => {
    if (canReviewRescheduleRequests) refresh();
  }, [location.pathname, canReviewRescheduleRequests, refresh]);

  const onReviewPage = location.pathname.includes(REVIEW_PATH);
  if (!canReviewRescheduleRequests || onReviewPage || count < 1 || maxId <= dismissedMaxId) {
    return null;
  }

  const countReschedule = Math.max(0, count - countNew);
  const parts: string[] = [];
  if (countNew > 0) parts.push(`${countNew} new appointment request${countNew === 1 ? '' : 's'}`);
  if (countReschedule > 0) parts.push(`${countReschedule} reschedule request${countReschedule === 1 ? '' : 's'}`);
  const breakdown = parts.length ? parts.join(' and ') : `${count} schedule request${count === 1 ? '' : 's'}`;

  // Sticky positioning lives in the shared banner wrapper in app.tsx (with CallbackQueueBanner).
  return (
    <div>
      <Alert
        banner
        type="warning"
        showIcon={false}
        message={
          <div style={{ textAlign: 'center', width: '100%' }}>
            <ExclamationCircleFilled style={{ color: '#faad14', marginRight: 8 }} />
            AI phone: <b>{breakdown}</b> waiting for review
          </div>
        }
        action={
          <Button size="small" type="primary" onClick={() => history.push(REVIEW_PATH)}>
            Review
          </Button>
        }
        closable
        onClose={() => {
          localStorage.setItem(LS_DISMISSED_KEY, String(maxId));
          setDismissedMaxId(maxId);
        }}
      />
    </div>
  );
};

export default AiRescheduleBanner;
